import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

const LINES = {
  en: [
    'The real you,',
    'the immortal you,',
    'is that which goes on from body to body,',
    'from life to life.',
  ],
  de: [
    'Das wahre Du,',
    'das unsterbliche Du,',
    'ist jenes, das von Körper zu Körper,',
    'von Leben zu Leben weiterbesteht.',
  ],
}
const AUTHOR = 'Brian L. Weiss MD'

const EN_LINES_IN = 600
const AUTHOR_IN = 3400
const EN_LINES_OUT = 6000
const DE_LINES_IN = 7200
const LINE_STAGGER = 500
// Total animation = 15s. Fade out 1.2s at the end.
const FADE_OUT_DURATION = 1200
const END = 15000
const ALL_OUT = END - FADE_OUT_DURATION

const STAGES = { idle: 0, countdown: 1, playing: 2, done: 3 }

function CosmicBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Shifting gradient base — color slowly breathes */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at 30% 30%, rgba(118,150,173,0.35) 0%, transparent 55%), radial-gradient(ellipse at 70% 70%, rgba(34,65,96,0.6) 0%, transparent 60%), radial-gradient(ellipse at 50% 50%, rgba(0,0,0,0.2) 60%, rgba(0,0,0,0.55) 100%)',
          animation: 'cosmic-breathe 14s ease-in-out infinite alternate',
        }}
      />

      {/* Drifting bloom 1 — faster, wider arc */}
      <div
        className="absolute rounded-full"
        style={{
          width: '120%',
          height: '60%',
          left: '-10%',
          top: '20%',
          background:
            'radial-gradient(circle, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.05) 40%, transparent 70%)',
          filter: 'blur(40px)',
          animation: 'cosmic-drift-1 12s ease-in-out infinite alternate',
        }}
      />

      {/* Drifting bloom 2 — opposite direction */}
      <div
        className="absolute rounded-full"
        style={{
          width: '80%',
          height: '50%',
          right: '-10%',
          bottom: '10%',
          background:
            'radial-gradient(circle, rgba(118,150,173,0.35) 0%, rgba(118,150,173,0.12) 50%, transparent 75%)',
          filter: 'blur(50px)',
          animation: 'cosmic-drift-2 16s ease-in-out infinite alternate',
        }}
      />

      {/* Orbital bloom — rotates around the center */}
      <div
        className="absolute rounded-full"
        style={{
          width: '50%',
          height: '50%',
          left: '25%',
          top: '25%',
          background:
            'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 60%)',
          filter: 'blur(30px)',
          animation: 'cosmic-orbit 20s linear infinite',
        }}
      />

      {/* Flowing white lines — energy streams with dash-flow animation */}
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 400 700"
        preserveAspectRatio="xMidYMid slice"
      >
        <g style={{ animation: 'cosmic-line-drift-1 10s ease-in-out infinite alternate' }}>
          <path
            d="M -50 200 Q 100 150 200 220 T 500 180"
            fill="none"
            stroke="rgba(255,255,255,0.28)"
            strokeWidth="1"
            strokeDasharray="4 8"
            style={{ animation: 'cosmic-flow 3s linear infinite' }}
          />
          <path
            d="M -50 240 Q 120 190 220 260 T 500 220"
            fill="none"
            stroke="rgba(255,255,255,0.15)"
            strokeWidth="0.8"
            strokeDasharray="3 10"
            style={{ animation: 'cosmic-flow 4s linear infinite reverse' }}
          />
        </g>
        <g style={{ animation: 'cosmic-line-drift-2 13s ease-in-out infinite alternate' }}>
          <path
            d="M 500 480 Q 320 430 220 500 T -50 460"
            fill="none"
            stroke="rgba(255,255,255,0.22)"
            strokeWidth="1"
            strokeDasharray="5 9"
            style={{ animation: 'cosmic-flow 3.5s linear infinite' }}
          />
          <path
            d="M 500 520 Q 300 470 200 540 T -50 500"
            fill="none"
            stroke="rgba(255,255,255,0.12)"
            strokeWidth="0.8"
            strokeDasharray="3 12"
            style={{ animation: 'cosmic-flow 5s linear infinite reverse' }}
          />
        </g>
        <g style={{ animation: 'cosmic-line-drift-3 16s ease-in-out infinite alternate' }}>
          <path
            d="M 200 -50 Q 250 200 180 400 T 220 750"
            fill="none"
            stroke="rgba(255,255,255,0.12)"
            strokeWidth="0.8"
            strokeDasharray="6 14"
            style={{ animation: 'cosmic-flow 6s linear infinite' }}
          />
        </g>

        {/* Stars — twinkle */}
        <circle cx="60" cy="100" r="1" fill="white" style={{ animation: 'cosmic-twinkle 3s ease-in-out infinite' }} />
        <circle cx="340" cy="150" r="0.8" fill="white" style={{ animation: 'cosmic-twinkle 4s ease-in-out infinite 0.5s' }} />
        <circle cx="80" cy="600" r="1.2" fill="white" style={{ animation: 'cosmic-twinkle 3.5s ease-in-out infinite 1s' }} />
        <circle cx="320" cy="580" r="0.8" fill="white" style={{ animation: 'cosmic-twinkle 4.5s ease-in-out infinite 1.5s' }} />
        <circle cx="200" cy="80" r="0.8" fill="white" style={{ animation: 'cosmic-twinkle 3s ease-in-out infinite 0.8s' }} />
        <circle cx="150" cy="650" r="0.8" fill="white" style={{ animation: 'cosmic-twinkle 4s ease-in-out infinite 2s' }} />
        <circle cx="280" cy="50" r="0.6" fill="white" style={{ animation: 'cosmic-twinkle 5s ease-in-out infinite 0.3s' }} />
        <circle cx="30" cy="400" r="0.6" fill="white" style={{ animation: 'cosmic-twinkle 3.5s ease-in-out infinite 1.8s' }} />
        <circle cx="370" cy="380" r="0.6" fill="white" style={{ animation: 'cosmic-twinkle 4s ease-in-out infinite 2.5s' }} />
        <circle cx="100" cy="280" r="0.5" fill="white" style={{ animation: 'cosmic-twinkle 3s ease-in-out infinite 0.2s' }} />
        <circle cx="310" cy="320" r="0.5" fill="white" style={{ animation: 'cosmic-twinkle 4s ease-in-out infinite 1.2s' }} />
      </svg>
    </div>
  )
}

export default function CaptureQuotePage() {
  const [params] = useSearchParams()
  const autoplay = params.get('autoplay') === '1'
  const variantParam = params.get('variant')

  const [variant, setVariant] = useState(variantParam === 'cosmic' ? 'cosmic' : null)
  const [stage, setStage] = useState(STAGES.idle)
  const [count, setCount] = useState(3)
  const [elapsed, setElapsed] = useState(0)

  const start = (v) => {
    setVariant(v)
    setStage(STAGES.countdown)
    setCount(3)
  }

  useEffect(() => {
    if (autoplay && stage === STAGES.idle) start(variantParam === 'cosmic' ? 'cosmic' : 'clean')
  }, [autoplay, stage, variantParam])

  useEffect(() => {
    if (stage !== STAGES.countdown) return
    if (count === 0) {
      setStage(STAGES.playing)
      setElapsed(0)
      return
    }
    const t = setTimeout(() => setCount((c) => c - 1), 1000)
    return () => clearTimeout(t)
  }, [stage, count])

  useEffect(() => {
    if (stage !== STAGES.playing) return
    const startTime = performance.now()
    let raf
    const tick = (now) => {
      const e = now - startTime
      setElapsed(e)
      if (e >= END) {
        setStage(STAGES.done)
        return
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [stage])

  const playing = stage === STAGES.playing || stage === STAGES.done
  const quoteMarkVisible = playing && elapsed > 0
  const authorVisible = playing && elapsed > AUTHOR_IN
  const enLineVisible = (i) =>
    playing && elapsed > EN_LINES_IN + i * LINE_STAGGER && elapsed < EN_LINES_OUT
  const deLineVisible = (i) =>
    playing && elapsed > DE_LINES_IN + i * LINE_STAGGER && elapsed < ALL_OUT
  const overallOpacity = elapsed > ALL_OUT
    ? Math.max(0, 1 - (elapsed - ALL_OUT) / FADE_OUT_DURATION)
    : 1

  return (
    <div className="fixed inset-0 bg-black flex items-center justify-center overflow-hidden">
      <div
        className="relative bg-color-primary overflow-hidden"
        style={{
          aspectRatio: '9 / 16',
          height: '100vh',
          maxWidth: '100vw',
        }}
      >
        {variant === 'cosmic' && <CosmicBackground />}

        {stage === STAGES.idle && !autoplay && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 z-20">
            <button
              onClick={() => start('clean')}
              className="typography-menu-text-on-dark border border-[var(--color-divider-on-dark)] rounded-full px-8 py-3 hover:bg-white/10 transition-colors w-56"
            >
              Clean
            </button>
            <button
              onClick={() => start('cosmic')}
              className="typography-menu-text-on-dark border border-[var(--color-divider-on-dark)] rounded-full px-8 py-3 hover:bg-white/10 transition-colors w-56"
            >
              Cosmic
            </button>
          </div>
        )}

        {stage === STAGES.countdown && (
          <div className="absolute inset-0 flex items-center justify-center z-20">
            <div
              key={count}
              className="text-primary-on-dark font-secondary"
              style={{
                fontSize: 'clamp(120px, 30vh, 240px)',
                animation: 'capture-count 1s ease-out',
              }}
            >
              {count > 1 ? count : ''}
            </div>
          </div>
        )}

        {playing && (
          <div
            className="absolute inset-0 flex items-center justify-center px-12 z-10"
            style={{ opacity: overallOpacity, transition: 'opacity 0ms' }}
          >
            <div className="w-full max-w-[320px] text-center">
              <div
                className="text-primary-on-dark mb-10 transition-all ease-out"
                style={{
                  transitionDuration: '800ms',
                  opacity: quoteMarkVisible ? 1 : 0,
                  transform: quoteMarkVisible ? 'scale(1)' : 'scale(0.7)',
                }}
              >
                <svg
                  className="mx-auto"
                  style={{ width: 'clamp(48px, 8vh, 72px)', height: 'clamp(48px, 8vh, 72px)' }}
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                </svg>
              </div>

              <div
                className="relative text-primary-on-dark font-secondary italic leading-[1.35]"
                style={{ fontSize: 'clamp(28px, 4.2vh, 44px)', minHeight: '36vh' }}
              >
                <blockquote className="relative">
                  {LINES.en.map((line, i) => (
                    <div
                      key={`en-${i}`}
                      className="transition-all ease-out"
                      style={{
                        transitionDuration: '900ms',
                        opacity: enLineVisible(i) ? 1 : 0,
                        transform: enLineVisible(i) ? 'translateY(0)' : 'translateY(20px)',
                      }}
                    >
                      {line}
                    </div>
                  ))}
                </blockquote>

                <blockquote className="absolute inset-0">
                  {LINES.de.map((line, i) => (
                    <div
                      key={`de-${i}`}
                      className="transition-all ease-out"
                      style={{
                        transitionDuration: '900ms',
                        opacity: deLineVisible(i) ? 1 : 0,
                        transform: deLineVisible(i) ? 'translateY(0)' : 'translateY(20px)',
                      }}
                    >
                      {line}
                    </div>
                  ))}
                </blockquote>
              </div>

              <div
                className="flex items-center justify-center gap-4 mt-16 transition-opacity ease-out"
                style={{
                  transitionDuration: '1200ms',
                  opacity: authorVisible ? 1 : 0,
                }}
              >
                <div className="h-px w-16 bg-gradient-to-r from-transparent to-[var(--color-divider-on-dark)]" />
                <span className="typography-label-on-dark whitespace-nowrap">{AUTHOR}</span>
                <div className="h-px w-16 bg-gradient-to-l from-transparent to-[var(--color-divider-on-dark)]" />
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes capture-count {
          0% { opacity: 0; transform: scale(1.4); }
          30% { opacity: 1; transform: scale(1); }
          100% { opacity: 0; transform: scale(0.9); }
        }
        @keyframes cosmic-drift-1 {
          0%   { transform: translate(-5%, -3%) scale(1); }
          100% { transform: translate(8%, 5%) scale(1.15); }
        }
        @keyframes cosmic-drift-2 {
          0%   { transform: translate(5%, 3%) scale(1.1); }
          100% { transform: translate(-8%, -5%) scale(1); }
        }
        @keyframes cosmic-line-drift-1 {
          0%   { transform: translate(0, 0); }
          100% { transform: translate(-15px, 10px); }
        }
        @keyframes cosmic-line-drift-2 {
          0%   { transform: translate(0, 0); }
          100% { transform: translate(12px, -8px); }
        }
        @keyframes cosmic-line-drift-3 {
          0%   { transform: translate(0, 0); }
          100% { transform: translate(-8px, -14px); }
        }
        @keyframes cosmic-breathe {
          0%   { filter: hue-rotate(0deg) brightness(1); transform: scale(1); }
          100% { filter: hue-rotate(-8deg) brightness(1.12); transform: scale(1.06); }
        }
        @keyframes cosmic-orbit {
          0%   { transform: translate(0, 0) rotate(0deg); }
          50%  { transform: translate(6%, -4%) rotate(180deg); }
          100% { transform: translate(0, 0) rotate(360deg); }
        }
        @keyframes cosmic-flow {
          0%   { stroke-dashoffset: 0; }
          100% { stroke-dashoffset: -40; }
        }
        @keyframes cosmic-twinkle {
          0%, 100% { opacity: 0.3; }
          50%      { opacity: 1; }
        }
      `}</style>
    </div>
  )
}
